import React from 'react';
import SubPage from '../components/site/SubPage';
import {
  ClosingBrand,
  PageSection,
  PageTitleBox,
  SectionHeading,
} from '../components/site/SubPageParts';

const IMG = '/images/site/abuse-playbook';

// The 20 techniques, per QA_NuV_Abuse Playbook.docx.
const TECHNIQUES = [
  {
    title: 'Gaslighting',
    body: 'Targets start to question their own memories, perceptions, and sanity. Abusers use a mixture of denial, misdirection, contradiction, and lying to produce feelings of confusion, anxiety, and uncertainty about their own reality.',
  },
  {
    title: 'Emotional Blackmail',
    body: 'Guilt, fear, or shame — withholding approval, love, or support until the target yields to the abuser’s wishes.',
  },
  {
    title: 'Psychological Manipulation',
    body: 'Lying, deceit, fake information or shading the truth to their own benefit, breaking down trust and leaving victims open to further manipulation.',
  },
  {
    title: 'Love Bombing',
    body: 'Quickly professing affection. Excessive praise designed to create a deep emotional bond, making it difficult for the victim to break off the interaction.',
  },
  {
    title: 'Emotional Confinement',
    body: 'Isolation creating dependency on the abuser. Convincing the subject to keep their connections hidden from friends, family, or any other sources of support, allowing the predator to exert control and restrict intervention.',
  },
  {
    title: 'Panic',
    body: 'Instilling feelings of anxiety, uncertainty, or dread, creating a sense of urgency and dependency on the abuser for protection or guidance.',
  },
  {
    title: 'Blame Game',
    body: 'Playing up their own suffering, portraying themselves as victims to elicit feelings of guilt leading to sympathy from the target.',
  },
  {
    title: 'Intimidation',
    body: 'Aggressive interaction to enforce compliance: verbal threats and demonstrations of authority.',
  },
  {
    title: 'Reflection',
    body: 'Build a common belief and trust with the target. Establishing an emotional connection allows the predator to put the target at ease, creating a trust bond and making the target open to being influenced and controlled.',
  },
  {
    title: 'Adulation',
    body: 'Using ongoing flattery and charm to disarm the target and move them through the different abuse phases. Predators appeal to the victim’s ego, establishing an emotional connection that obscures rational thinking.',
  },
  {
    title: 'Mental Conflict',
    body: 'Having two different beliefs creating a conflict between emotions and logic. Abusers will introduce contradictory information, sowing confusion in their target.',
  },
  {
    title: 'Camel nose under the tent',
    body: 'Coaching someone to agree to a small request before presenting a larger one. Once a person has agreed to something small, they are more likely to agree to something larger later.',
  },
  {
    title: 'Switcheroo',
    body: 'Coaches the victim with appealing promises to change their life conditions. A key technique in catfishing (romance), pig butchering (cryptocurrency) or other types of investment fraud.',
  },
  {
    title: 'Authority Exploitation',
    body: 'Amplify their authority, expertise or life experience to build trust and influence the target to act. Presenting themselves as knowledgeable enables them to gain the trust and compliance of their victims.',
  },
  {
    title: 'Scammer-in-the-middle',
    body: 'Create discord and rivalry between the targets and their family and friends. This ensures the target will remain dependent upon the abuser throughout the abuse lifecycle.',
  },
  {
    title: 'Reverse psychology',
    body: 'Designed to evoke a specific reaction or behavior aligning with the abusers’ true intentions. Ideas and suggestions are presented in a reverse manner. Often the target is unaware the manipulation is occurring.',
  },
  {
    title: 'Degradation',
    body: 'Abusers use shaming and humiliation to exert control over their targets. It undermines a person’s judgement, self-esteem and sense of worth, increasing the victim’s reliance on the abuser.',
  },
  {
    title: 'Filtration',
    body: 'Seduction or sexualizing to gain or maintain control. By exploiting their targets’ emotional and physical desires, manipulators gain control and influence over the target.',
  },
  {
    title: 'Brainwashing',
    body: 'An emotional connection — empathy. Abusers use a combination of kindness and manipulation alongside the abuse, leading the victim to sympathize with and defend their abusers.',
  },
  {
    title: 'Playing the Victim Card',
    body: 'Frequently predators adopt the role of the victim to evoke sympathy, induce feelings of guilt, or divert attention from their own deceitful behavior. They present themselves as powerless, unjustly treated, or unfairly maligned to coerce others into providing support or fulfilling their own selfish desires.',
  },
];

// The six stages, in order. Set as a plain list gathered by a brace, per the
// mockup -- they were a chain of darkening chevrons before.
const LIFECYCLE = [
  'Identify the target',
  'Build trust',
  'Fill a need',
  'Isolate',
  'Sexualize (Romance)',
  'Control',
];

/*
 * The brace that gathers the six stages. Drawn rather than set as a glyph so it
 * stretches to whatever height the list happens to be: the viewBox is squashed
 * vertically to fit, and non-scaling-stroke keeps the line an even weight while
 * that happens.
 */
const LifecycleBrace = () => (
  <svg
    className="h-full w-[1.2em] flex-shrink-0 text-black"
    viewBox="0 0 36 300"
    preserveAspectRatio="none"
    aria-hidden="true"
    focusable="false"
  >
    <path
      d="M6 2 Q18 2 18 16 L18 132 Q18 148 32 150 Q18 152 18 168 L18 284 Q18 298 6 298"
      fill="none"
      stroke="currentColor"
      strokeWidth="3"
      strokeLinecap="round"
      vectorEffect="non-scaling-stroke"
    />
  </svg>
);

const AbusePlaybook = () => (
  <SubPage title="Abuse Playbook">
    <PageTitleBox>Liars / Cheaters / Abuser.</PageTitleBox>

    <PageSection className="pt-4">
      <img
        src={`${IMG}/predator-and-target.jpeg`}
        alt="Split illustration: a man messaging from a dim room, a young woman smiling at her screen"
        className="w-full object-cover"
      />
    </PageSection>

    {/* ---------------- Overview ---------------- */}
    <PageSection className="pt-10">
      <SectionHeading>Techniques, Strategies, Approaches.</SectionHeading>
      <p className="mt-4 text-[15px] leading-relaxed text-black md:text-[18px]">
        The Abuse Playbook has 6 different integrated stages comprised of 20 different techniques
        to exploit individuals&rsquo; emotional vulnerabilities. It focuses on the psychological
        manipulation techniques used by predators to influence and control their victims. These
        techniques have a single purpose &mdash; influence the target&rsquo;s decision-making to
        achieve the predators&rsquo; goals. This has a long-lasting impact upon the victim&rsquo;s
        mental health, leading to issues such as anxiety, depression, and low self-esteem in those
        who are victimized.
      </p>
    </PageSection>

    <PageSection className="pt-8">
      <img
        src={`${IMG}/profiles.jpeg`}
        alt="Four faces in profile, lit from bright to dark"
        className="h-[240px] w-full object-cover small:aspect-[14/5] small:h-auto"
      />
    </PageSection>

    {/* ---------------- Techniques ---------------- */}
    <PageSection className="pt-10">
      <SectionHeading>Abuse Playbook.</SectionHeading>
      <ol className="mt-5 grid grid-cols-1 gap-x-8 gap-y-5 md:grid-cols-2">
        {TECHNIQUES.map((technique, index) => (
          <li
            key={technique.title}
            className="border-b border-black/15 bg-[#FAFAFA] px-5 py-4"
          >
            <h3 className="text-[15px] font-bold text-black md:text-[16px]">
              {index + 1} {technique.title}
            </h3>
            <p className="mt-2 text-[13px] leading-snug text-black/80 md:text-[14px]">
              {technique.body}
            </p>
          </li>
        ))}
      </ol>
    </PageSection>

    {/* ---------------- Lifecycle ---------------- */}
    <PageSection className="pt-12">
      <SectionHeading>Abuse lifecycle.</SectionHeading>
      {/* Stages, brace and artwork are one block that never wraps: the list
          sits in the left half with the brace gathering it, the artwork holds
          the right half at every width. Everything is sized from the row's own
          font-size -- the brace width, both gaps and the list spacing are all
          in em -- so the single clamp below scales the whole block down with
          the viewport instead of each piece stepping on its own. The clamp's
          upper bound is the mockup's 34px; its slope keeps the longest stage,
          "Sexualize (Romance)", inside the left half down to a 320px screen. */}
      <div className="mt-6 flex items-center gap-[2%] text-[clamp(9px,3.35vw,34px)]">
        <div className="flex min-w-0 flex-1 items-stretch justify-center gap-[0.4em]">
          <ol className="flex flex-col justify-center gap-[0.5em] text-center leading-snug text-black">
            {LIFECYCLE.map((stage) => (
              <li key={stage} className="whitespace-nowrap">
                {stage}
              </li>
            ))}
          </ol>
          <LifecycleBrace />
        </div>
        <img
          src={`${IMG}/puppet-hands.jpeg`}
          alt="A pair of hands working marionette strings, in black and white"
          className="aspect-[16/15] w-1/2 flex-shrink-0 object-cover"
        />
      </div>
    </PageSection>

    {/* ---------------- Closing ----------------
        These two land the page: naming the behaviour is what lets you avoid
        it. Nothing follows them — the old "Easy to use. Easy to love."
        wordmark block was removed so the page ends here. */}
    <PageSection className="pt-12">
      <SectionHeading>To know you.</SectionHeading>
      <img
        src={`${IMG}/to-know-you.jpeg`}
        alt=""
        className="mt-5 w-full object-cover"
      />
    </PageSection>

    <PageSection className="pt-12">
      <SectionHeading>Is to avoid you.</SectionHeading>
      <img
        src={`${IMG}/is-to-avoid-you.jpeg`}
        alt=""
        className="mt-5 w-full object-cover"
      />
    </PageSection>

    {/* Footer per slide 41. */}
    <ClosingBrand tagline="Information. Knowledge. Awareness." />
  </SubPage>
);

export default AbusePlaybook;
